import { Fragment, useEffect, useState } from 'react';
import { myCopyText } from '../../../../../../../utils/myCopyText';
import {
  SHIPMENT_STATUS_ID_STOCK_ORDER,
  SHIPMENT_STATUS_OPTION_LIST_STOCK_ORDER,
  SHIPMENT_STATUS_STOCK_ORDER
} from '../../../../../../../types/stockOrders';
import { copyClipboard } from '../../../../../../../utils/copyClipboard';
import { ObjectKey } from '../../../../../../../types/pills';
import TrackingAndDatesSection from './TrackingAndDatesSection/TrackingAndDatesSection';
import { IconEditOrders } from '../../../../../../../assets/DesignSystem/SVGComponents';
import { IStockOrderDetail } from '../../../../../../../types/stockOrders';
import { formatShipmentForDelivery } from '../../../../../../../utils/formatShipmentsForDelivery';
import { NudosHoverText } from '../../../../../../../components/NudosComponents';
import { CopyIcon } from '../../../../../../../assets/images/shopping';
import { ICountry, IGeneralData, IOption, IProductDelivery, IShipmentData } from '../../../../../../../types/orders';
import NewEditSkuModal from '../../../../../../orders/Components/EditSkuModal/NewEditSkuModal';
import { autoUpdateSku } from '../../../../../../../services/traking.services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import { AxiosError } from 'axios';
import SerialsModal from '../../../../../../orders/Components/SerialsModal/SerialsModal';
import { formatSpecs } from '../../../../../../../utils/formatSpecs';
import DeliveryProcessModal from '../../../../../../orders/Components/DeliveryProcessModal/DeliveryProcessModal';
import DeliveryOrderModal from '../../../../../../../components/DeliveryOrderModal/DeliveryOrderModal';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import './StockOrderShipmentCard.scss';

const StockOrderShipmentCard = ({
  stockOrderId,
  shipment,
  shipments,
  isEnabled,
  country,
  updateWithoutLoaders
}: {
  shipment: IStockOrderDetail['generalData']['shipments'][number];
  shipments: IStockOrderDetail['generalData']['shipments'];
  isEnabled: boolean;
  updateWithoutLoaders: () => Promise<void>;
  stockOrderId: number;
  country: ICountry;
}) => {
  const [editSkuModal, setEditSkuModal] = useState<IProductDelivery>();
  const [showSerialsModal, setShowSerialsModal] = useState<IProductDelivery>();
  const [showProcessModal, setShowProcessModal] = useState<boolean>(false);
  const [isEditedProcessModal, setIsEditedProcessModal] = useState<boolean>(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState<boolean>(false);
  const [selectOption, setSelectOption] = useState<IOption>();
  const [notFoundSku, setNotFoundSku] = useState<boolean>(false);
  const [loadingSku, setLoadingSku] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [idLoad, setIdLoad] = useState<number>();

  const { status, product, number, quantity, stockShipmentId } = shipment;

  const allowSkuEditing = [SHIPMENT_STATUS_STOCK_ORDER.DELIVERY_PROCESS, 'Confirmado'].includes(
    status as SHIPMENT_STATUS_STOCK_ORDER
  );

  const formatDataTracking = formatShipmentForDelivery(shipment);

  const generalDataTransformed = {
    orgData: {
      organizationName: 'Bodegas Stock'
    },
    shipmentData: shipments?.map(formatShipmentForDelivery) as IShipmentData[]
  };

  const handleCopyProductInfo = () => {
    copyClipboard({
      text: myCopyText({
        productInfo: {
          ...product,
          id: stockShipmentId,
          unit: quantity?.toString(),
          name: product.name,
          pills: product.pills as unknown as ObjectKey<string>
        }
      })
    });
  };

  const handleChangeFoundSku = (error: boolean) => setNotFoundSku(error);

  const updateSKU = async (newSKU: string) => {
    setLoadingSku(true);
    const productIds = shipment.items.map(item => item.productId);
    const bodySkus = {
      productsIds: productIds,
      newSku: newSKU?.replaceAll(' ', '')
    };
    try {
      await autoUpdateSku(bodySkus);
      await updateWithoutLoaders?.();
      setEditSkuModal(undefined);
      displaySuccessNotification({
        customJSXMessage: <>Se realizo la solicitud de manera correcta, en un rato debes encontrar el SKU actualizado</>
      });
    } catch (error) {
      const err = error as AxiosError;
      if (err?.response?.data?.message === 'sku not found') {
        handleChangeFoundSku(true);
        displayErrorNotification({
          customJSXMessage: <>Este SKU no existe en el catalogo</>
        });
      }
    } finally {
      setLoadingSku(false);
    }
  };

  const changeStatusLogic = async (selectOption: IOption) => {
    if (!selectOption) return;
    const selectedStatus = selectOption.ShipmentStatusId;
    const currentStatus = shipment?.statusId;
    const shipmentId = stockShipmentId;

    setIdLoad(shipmentId);
    try {
      // Transición de confirmado a proceso de entrega
      if (
        currentStatus === SHIPMENT_STATUS_ID_STOCK_ORDER.CONFIRMED &&
        selectedStatus === SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERY_PROCESS
      ) {
        setShowProcessModal(true);
        return;
      }

      // Transición de proceso de entrega o atrasado a entregado
      if (
        (currentStatus === SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERY_PROCESS ||
          currentStatus === SHIPMENT_STATUS_ID_STOCK_ORDER.DELAYED) &&
        selectedStatus === SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERED
      ) {
        setShowDeliveryModal(true);
        return;
      }
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIdLoad(undefined);
    }
  };

  const disabledSelect = (selectId: number, statusId: number) => {
    // Si es el estado actual, no se muestra
    if (selectId === statusId) return { show: false, enabled: false };

    // Si el estado es atrasado, solo mostrar y habilitar Entregada y Cancelada
    if (statusId === SHIPMENT_STATUS_ID_STOCK_ORDER.DELAYED) {
      if (
        selectId === SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERED ||
        selectId === SHIPMENT_STATUS_ID_STOCK_ORDER.CANCELLED
      ) {
        return { show: true, enabled: true };
      }
      return { show: false, enabled: false };
    }

    // No se pueden seleccionar estados anteriores
    if (selectId < statusId) return { show: false, enabled: false };

    // Caso especial: De proceso de entrega a entregado
    if (
      statusId === SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERY_PROCESS &&
      selectId === SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERED
    ) {
      return { show: true, enabled: true };
    }

    // Solo se puede avanzar al siguiente estado válido
    const nextValidState = SHIPMENT_STATUS_OPTION_LIST_STOCK_ORDER.find(
      option => option.ShipmentStatusId > statusId
    )?.ShipmentStatusId;

    if (selectId === nextValidState) return { show: true, enabled: true };

    // Todos los estados futuros deben mostrarse pero deshabilitados
    return { show: true, enabled: false };
  };

  const generateOptions = (shipmentData: IStockOrderDetail['generalData']['shipments'][number]) => {
    return SHIPMENT_STATUS_OPTION_LIST_STOCK_ORDER.map((item, index) => {
      const { enabled: renderDisabledSelectEnabled, show: renderDisabledSelectShow } = disabledSelect(
        item?.ShipmentStatusId,
        shipmentData?.statusId || 0
      );

      return (
        <Fragment key={`option-${item}-index${index}`}>
          {renderDisabledSelectShow && (
            <div
              className="optionStyle"
              onClick={() => {
                if (renderDisabledSelectEnabled) changeStatusLogic(item);
              }}
            >
              <div
                className={`optionText ${!renderDisabledSelectEnabled ? 'disabledSelect' : ''}`}
                role="button"
                tabIndex={renderDisabledSelectEnabled ? 0 : -1}
              >
                {item?.name}
              </div>
            </div>
          )}
        </Fragment>
      );
    });
  };

  const assingStatus = () => {
    const findState = SHIPMENT_STATUS_OPTION_LIST_STOCK_ORDER?.find(
      element => shipment?.statusId === element.ShipmentStatusId
    );
    if (findState) setSelectOption(findState);
  };

  useEffect(() => {
    if (shipment?.status) assingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment]);

  return (
    <div className="stockOrderShipmentCard">
      {showProcessModal && (
        <DeliveryProcessModal
          changeShowModal={() => setShowProcessModal(false)}
          orderId={stockOrderId}
          updateDataTraking={updateWithoutLoaders}
          dataTraking={formatDataTracking as IShipmentData}
          isEdited={isEditedProcessModal}
          listOfGroupedShipments={shipments?.map(formatShipmentForDelivery) as IShipmentData[]}
          generalData={generalDataTransformed as IGeneralData}
          selectOption={selectOption}
          isStockOrder
          receiverName={product?.name}
          flag={country?.flag}
        />
      )}
      {showDeliveryModal && (
        <DeliveryOrderModal
          closeModal={() => setShowDeliveryModal(false)}
          updateDeliveryOrder={updateWithoutLoaders}
          orderId={stockOrderId.toString()}
          dataTraking={formatDataTracking as IShipmentData}
          listOfGroupedShipments={shipments?.map(formatShipmentForDelivery) as IShipmentData[]}
          organizationName="Bodegas Stock"
          receiverName={shipment?.product?.name}
          flag={country?.flag}
          isStockOrder
        />
      )}
      {showSerialsModal && (
        <SerialsModal
          productInfo={{
            name: product?.name,
            pills: product?.pills as unknown as ObjectKey<string>,
            sku: product?.sku,
            units: quantity ?? 0
          }}
          singleProduct={showSerialsModal}
          changeShowModal={() => setShowSerialsModal(undefined)}
          updateWithoutLoaders={updateWithoutLoaders}
          products={shipment?.items?.map(item => ({
            id: item.productId,
            name: shipment.product.name,
            pills: shipment.product.pills as unknown as ObjectKey<string>,
            sku: shipment.product.sku,
            serial: item.serial,
            saleId: undefined,
            isRequestedChange: false
          }))}
          isStockOrder
        />
      )}
      {editSkuModal && (
        <div onClick={e => e.stopPropagation()}>
          <NewEditSkuModal
            defaultSku={editSkuModal?.sku || ''}
            handleChangeFoundSku={() => handleChangeFoundSku(false)}
            notFoundSku={notFoundSku}
            closeModal={() => {
              handleChangeFoundSku(false);
              setEditSkuModal(undefined);
            }}
            updateSKU={updateSKU}
            loadingSku={loadingSku}
            productInfo={{ name: editSkuModal?.name, pills: editSkuModal?.pills, units: Number(editSkuModal.unit) }}
          />
        </div>
      )}
      <div>
        <div className="text-nudos-text-color font-extrabold text-xss">Producto N° {number}</div>
        <div className="flex items-start justify-between mt-8">
          <div className="flex items-start gap-16">
            <div className="unitsContainer">
              <div className="grayTitle mb-8">Und.</div>
              <div className="valueFontNormal">{quantity}</div>
            </div>
            <div className="infoProductDetailsContainer">
              <div className="grayTitle mb-8">Producto</div>
              <div className="textProductDetails">
                <div className="textLimits text-nudos-text-color font-semibold">{product?.name}</div>
                <NudosHoverText
                  text={formatSpecs(product?.pills as unknown as ObjectKey<string>)}
                  charactersLimit={30}
                  onlyIfTruncated
                  customTextHoverClassName="customTextHoverClassName text-nudos-text-color"
                />
                <div className="shipmentCopyIconWrapper" onClick={handleCopyProductInfo}>
                  <CopyIcon stroke="#383838" />
                </div>
              </div>
              <div className="skuProduct">
                <span> SKU Catalogo: {product?.sku}</span>
                {allowSkuEditing && (
                  <div
                    className="editAction cursor-pointer"
                    onClick={() => {
                      setEditSkuModal({
                        id: stockShipmentId,
                        name: product?.name,
                        pills: product?.pills as unknown as ObjectKey<string>,
                        sku: product?.sku
                      });
                    }}
                  >
                    <IconEditOrders />
                  </div>
                )}
              </div>
              <div
                className="addSerials"
                onClick={() => {
                  setShowSerialsModal({
                    id: stockShipmentId,
                    name: product?.name ?? '',
                    pills: product?.pills as unknown as ObjectKey<string>,
                    sku: product?.sku ?? '',
                    serial: shipment?.items[0]?.serial ?? '',
                    saleId: undefined,
                    isRequestedChange: false
                  });
                }}
              >
                <span className="addSerialsText">
                  {shipment?.items?.some(item => item.serial) ? 'Ver/editar seriales' : 'Agregar seriales'}
                </span>
                <div className="editAction">
                  <IconEditOrders />
                </div>
              </div>
            </div>
            <div className="shipmentStatus">
              <div className="grayTitle">Estado</div>
              <div
                className="boxStatus"
                onMouseOut={() => !isEnabled && !idLoad && setShowMessage(true)}
                onMouseLeave={() => setShowMessage(false)}
              >
                <BordSelectDropdown
                  isFilled
                  isLoading={idLoad === stockShipmentId}
                  isDeactivated={
                    !isEnabled || selectOption?.ShipmentStatusId === SHIPMENT_STATUS_ID_STOCK_ORDER.DELIVERED
                  }
                  selectOptions={generateOptions(shipment)}
                  currentValueOrplaceholder={status || 'Pendiente de confirmac..'}
                  componentSize="small"
                />
                {showMessage &&
                  shipment?.status !== SHIPMENT_STATUS_STOCK_ORDER.DELIVERED &&
                  idLoad !== shipment?.stockShipmentId && (
                    <div className="floatMessage">
                      Para cambiar el estado debemos recibir el pago. En caso de ser un método de pago manual cambiálo
                      en el Trello cargando el comprobante
                    </div>
                  )}
              </div>
            </div>
          </div>
          <div>
            <div className="grayTitle mb-8">Información de tracking</div>
            <TrackingAndDatesSection
              statusLog={shipment?.statusLog || []}
              tracking={{
                trackingLink: shipment?.tracking?.trackingLink || '',
                trackingCode: shipment?.tracking?.trackingCode || '',
                estimatedDeliveryDate: shipment?.tracking?.estimatedDeliveryDate || ''
              }}
              countryTimeZone={0}
              setShowProcessModal={setShowProcessModal}
              setIsEditedProcessModal={setIsEditedProcessModal}
            />
          </div>
        </div>
        <div className="customDivider medium" />
      </div>
    </div>
  );
};

export default StockOrderShipmentCard;
